import { useContext, useEffect, useCallback, useMemo } from 'react';

import { isEqual } from 'lodash';

import type EntityContext from 'common/constants/entityContexts';
import type Permissions from 'common/constants/permissions';
import { useCustomMemo } from 'common/helpers/useCustomMemo';

import PermissionsContext from './PermissionsContext';
import type { Permission, PermissionsQueryVariables } from './queries';

export type UsePermissionsInput = {
  context?: EntityContext;
  entityId?: string;
  permissionNames?: Permissions[];
  skip?: boolean;
};

export type UsePermissionsOutput = {
  loading: boolean;
  variables?: PermissionsQueryVariables;
  hasPermission: (permissionName: Permissions) => boolean;
  hasAllOf: (permissionNames: Permissions[]) => boolean;
  hasSomeOf: (permissionNames: Permissions[]) => boolean;
};

export type UsePermissionsType = (input: UsePermissionsInput) => UsePermissionsOutput;

export default function usePermissions({
  permissionNames,
  context,
  skip,
  entityId,
}: UsePermissionsInput): UsePermissionsOutput {
  const {
    permissions: contextScopes,
    unregisterPermissions,
    registerPermissions,
    variables,
  } = useContext(PermissionsContext);

  // All permissions for the given context and entityId that are currently loaded.
  const scopePermissions: Permission[] | undefined = useMemo(
    () =>
      contextScopes.find(item => item.context === (context ?? null) && item.entityId === (entityId ?? null))
        ?.permissions,
    [contextScopes, context, entityId]
  );

  const permissionNamesMemoized = useCustomMemo<Permissions[] | undefined>(permissionNames, isEqual);

  const arePermissionsLoaded = useMemo(() => {
    const existingPermissionNames = scopePermissions?.map(({ permissionName }) => permissionName);
    return (
      permissionNamesMemoized?.every(permission => existingPermissionNames?.includes(permission)) ??
      !!existingPermissionNames
    );
  }, [scopePermissions, permissionNamesMemoized]);

  useEffect(() => {
    const subscriptionId = !skip
      ? registerPermissions({
          permissionNames: permissionNamesMemoized,
          context,
          entityId,
        })
      : undefined;

    return () => {
      if (subscriptionId) {
        unregisterPermissions(subscriptionId);
      }
    };
  }, [skip, context, entityId, permissionNamesMemoized, registerPermissions, unregisterPermissions]);

  const hasPermission = useCallback(
    (permissionName: Permissions): boolean =>
      !!scopePermissions?.some(item => item.hasAccess && item.permissionName === permissionName),
    [scopePermissions]
  );

  const hasSomeOf = useCallback(
    (permissions?: Permissions[]): boolean => !!permissions?.some(hasPermission),
    [hasPermission]
  );

  const hasAllOf = useCallback(
    (permissions?: Permissions[]): boolean => !!permissions?.every(hasPermission),
    [hasPermission]
  );

  return {
    variables,
    loading: !arePermissionsLoaded,
    hasPermission,
    hasAllOf,
    hasSomeOf,
  };
}
